<script lang="ts">
    import '../global.css'
    import ErrorLogger from '#cmp/shared/providers/LoggerProvider.svelte'
    import PageTransition from '#cmp/shared/providers/PageTransition.svelte'
    import { page } from '$app/state'
    import ThemeProvider from '#cmp/shared/providers/ThemeProvider.svelte'
    import PromptProvider from '#cmp/shared/providers/PromptProvider.svelte'
    import Footer from '#cmp/shared/layout/Footer.svelte'
    import { onMount } from 'svelte'
    import { ScopedTheme, ThemeStore } from '#stores/themeStore.svelte.ts'
    import { beforeNavigate } from '$app/navigation'
    import { navigationStore } from '#stores/navigationStore.ts'
    import { toAbsoluteUrl } from '#lib/seo.ts'

    interface Props {
        children?: import('svelte').Snippet
    }

    let { children }: Props = $props()
    let metaTheme: HTMLMetaElement | null = $state(null)

    // Emitted once here rather than in each of the ~40 pages that write their own
    // head block: both values derive from the URL, so a page has nothing to add. The
    // exception is a page that duplicates another, such as an instruction's other
    // spelling, which names the page it duplicates as `canonicalPath`.
    let canonicalUrl = $derived(toAbsoluteUrl(page.data.canonicalPath ?? page.url.pathname))
    let socialImage = $derived(
        page.data.socialImage ?? {
            src: '/images/ASM-editor.webp',
            alt: 'The ASM Editor assembly Workbench'
        }
    )

    onMount(() => {
        // The service worker is registered automatically by SvelteKit from
        // src/service-worker/index.ts — no manual registration needed here.
        metaTheme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    })
    beforeNavigate((p) => {
        if (p.shallow && p.type === 'goto') return
        if (!p.to || !p.from) return

        navigationStore.navigatingTo(p.to.url.pathname, p.from.url.pathname, p.to.url.searchParams)
    })

    const color = ThemeStore.theme.secondary

    $effect(() => {
        if (metaTheme) {
            //a subtree showing a language's colours dresses the browser chrome too: the tag lives
            //in this head, so it is the one part of that theme the subtree cannot write itself
            metaTheme.content = ScopedTheme.theme?.theme.secondary.color ?? color.color
        }
    })
</script>

<svelte:head>
    <link rel="canonical" href={canonicalUrl} />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:site_name" content="Asm Editor" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta property="og:image" content={toAbsoluteUrl(socialImage.src)} />
    <meta property="og:image:alt" content={socialImage.alt} />
    <meta name="twitter:image" content={toAbsoluteUrl(socialImage.src)} />
    <meta name="twitter:image:alt" content={socialImage.alt} />
</svelte:head>

<ThemeProvider>
    <ErrorLogger>
        <PromptProvider>
            <PageTransition refresh={page.url.pathname} />
            {@render children?.()}

            <!-- fix this -->
            <Footer
                pages={[
                    /^\/projects$/,
                    /^\/projects\/create$/,
                    /^\/learn\/courses$/,
                    /^\\$/,
                    /^\/$/,
                    /^\/donate$/,
                    /^\/changelog$/,
                    /^\/documentation$/
                ]}
            />
        </PromptProvider>
    </ErrorLogger>
</ThemeProvider>
